import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, Modal, Slide } from '@mui/material';
import Button from '../ui/Button';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { Case, MeetingNote } from '../../types';
import { logMeetingNotesAdded } from '../../utils/activityLogger';
import { t } from '../../utils/translations';
import {
  buildSessionSelectOptions,
  defaultSessionForNewNote,
  parseMeetingNoteDoc,
  sessionSelectLabel,
} from './meetingNotesUtils';

interface MeetingNoteFormDialogProps {
  open: boolean;
  caseItem: Case | null;
  reportCount?: number;
  /** When set, dialog opens in edit mode. */
  editingNote?: MeetingNote | null;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Simple add/edit note dialog (session dropdown + content) —
 * same UX as Jurnalul sesiunii page composer.
 */
const MeetingNoteFormDialog: React.FC<MeetingNoteFormDialogProps> = ({
  open,
  caseItem,
  reportCount = 0,
  editingNote = null,
  onClose,
  onSaved,
}) => {
  const { currentUser } = useAuth();
  const [noteContent, setNoteContent] = useState('');
  const [sessionNumber, setSessionNumber] = useState(0);
  const [existingNotes, setExistingNotes] = useState<MeetingNote[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const sessionOptions = useMemo(
    () => buildSessionSelectOptions(reportCount, existingNotes),
    [reportCount, existingNotes]
  );

  // Derived instead of stateful so switching between add and edit never renders
  // one frame with the fields from the previous opening.
  const requestKey =
    open && caseItem ? `${caseItem.id}:${editingNote?.id ?? 'new'}` : null;
  const initializing = requestKey !== null && loadedFor !== requestKey;

  useEffect(() => {
    if (!open || !caseItem) return;

    let cancelled = false;
    const load = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'meetingNotes'), where('caseId', '==', caseItem.id))
        );
        if (cancelled) return;
        const notes = snap.docs.map((d) => parseMeetingNoteDoc(d.id, d.data()));
        setExistingNotes(notes);
        if (editingNote) {
          setNoteContent(editingNote.content);
          setSessionNumber(
            editingNote.sessionNumber ??
              defaultSessionForNewNote(buildSessionSelectOptions(reportCount, notes))
          );
        } else {
          setNoteContent('');
          setSessionNumber(
            defaultSessionForNewNote(buildSessionSelectOptions(reportCount, notes))
          );
        }
        setError(null);
        setLoadedFor(requestKey);
      } catch (err) {
        console.error('Error loading notes for form:', err);
        if (!cancelled) {
          setExistingNotes([]);
          setNoteContent(editingNote?.content ?? '');
          setSessionNumber(
            editingNote?.sessionNumber ?? defaultSessionForNewNote([0])
          );
          setLoadedFor(requestKey);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, caseItem, editingNote, reportCount, requestKey]);

  if (!caseItem) return null;

  const handleClose = () => {
    if (saveLoading) return;
    onClose();
  };

  const handleSave = async () => {
    if (!noteContent.trim() || saveLoading) return;
    try {
      setSaveLoading(true);
      setError(null);
      if (editingNote) {
        await updateDoc(doc(db, 'meetingNotes', editingNote.id), {
          content: noteContent.trim(),
          sessionNumber,
          updatedAt: new Date(),
        });
      } else {
        await addDoc(collection(db, 'meetingNotes'), {
          caseId: caseItem.id,
          content: noteContent.trim(),
          sessionNumber,
          createdBy: currentUser?.id || '',
          createdByName: currentUser?.fullName || '',
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        if (currentUser) {
          await logMeetingNotesAdded(
            caseItem.id,
            caseItem.title,
            currentUser.id,
            currentUser.fullName || currentUser.email || 'Unknown User'
          );
        }
      }
      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving meeting note:', err);
      setError(t.meetingNotes.addNoteError);
    } finally {
      setSaveLoading(false);
    }
  };

  // A plain fixed div cannot be typed into when this opens on top of another
  // MUI modal (the mobile session drawer), because that modal keeps trapping
  // focus. Going through Modal registers this dialog as the topmost one.
  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      aria-labelledby="meeting-note-form-title"
      sx={{ zIndex: 1400 }}
      slotProps={{
        backdrop: {
          timeout: { enter: 260, exit: 200 },
          sx: { backgroundColor: 'rgba(15, 23, 42, 0.4)' },
        },
      }}
    >
      <Slide
        direction="up"
        in={open}
        timeout={{ enter: 260, exit: 200 }}
        onExited={() => setLoadedFor(null)}
      >
        <div className="safe-pt safe-pb fixed inset-0 flex items-end justify-center outline-none sm:items-center sm:p-4">
          <div className="relative flex h-full w-full flex-col overflow-hidden bg-white shadow-lg sm:h-auto sm:max-h-[min(85vh,100%)] sm:max-w-md sm:rounded-xl sm:border sm:border-slate-200">
            <div className="shrink-0 border-b border-slate-100 px-4 pb-3 pt-4 sm:border-b-0 sm:px-5 sm:pb-0 sm:pt-5">
              <h2
                id="meeting-note-form-title"
                className="text-base font-semibold text-slate-900"
              >
                {editingNote ? t.meetingNotes.editNote : t.meetingNotes.addNote}
              </h2>
              <p className="mt-1 truncate text-sm text-slate-500">
                {caseItem.counseledName} · {caseItem.title}
              </p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-3 sm:px-5">
              {initializing ? (
                <div className="flex min-h-[14rem] flex-1 items-center justify-center">
                  <CircularProgress size={26} sx={{ color: '#C99700' }} />
                </div>
              ) : (
                <>
                  <label className="block shrink-0 text-sm font-medium text-slate-800">
                    {t.meetingNotes.sessionField}
                    <select
                      value={sessionNumber}
                      onChange={(e) => setSessionNumber(Number(e.target.value))}
                      disabled={saveLoading}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:opacity-50 sm:text-sm"
                    >
                      {sessionOptions.map((n) => (
                        <option key={n} value={n}>
                          {sessionSelectLabel(n, reportCount)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="mt-3 flex min-h-0 flex-1 flex-col text-sm font-medium text-slate-800">
                    {t.meetingNotes.content}
                    <textarea
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      disabled={saveLoading}
                      rows={4}
                      placeholder={t.meetingNotes.contentPlaceholder}
                      className="mt-1 min-h-[8rem] w-full flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-base text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:opacity-50 sm:flex-none sm:text-sm"
                    />
                  </label>

                  {error && (
                    <p className="mt-2 shrink-0 text-sm text-red-600">{error}</p>
                  )}
                </>
              )}
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-slate-100 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:border-t-0 sm:px-5 sm:pb-5 sm:pt-1">
              <Button onClick={handleClose} disabled={saveLoading}>
                {t.common.cancel}
              </Button>
              <Button
                variant="primary"
                loading={saveLoading}
                disabled={initializing || !noteContent.trim()}
                onClick={() => void handleSave()}
              >
                {editingNote ? t.common.save : t.meetingNotes.addNote}
              </Button>
            </div>
          </div>
        </div>
      </Slide>
    </Modal>
  );
};

export default MeetingNoteFormDialog;
